/**
 * MobileProfile — Logged-in user's profile with editable food
 * preferences, food-assistance enrollment, and loyalty-program cards.
 *
 * Route: /m/profile — auth-required (redirects guests to /login).
 */
import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Pencil, Trash2, LogOut, Mail, Phone, Leaf } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const FP_PURPLE = "#653091";

const DIET_TAGS = [
  { key: "vegetarian", label: "Vegetarian", Icon: Leaf, color: "#4CAF50" },
  { key: "organic",    label: "Organic",    Icon: Leaf, color: "#4CAF50" },
  { key: "kosher",     label: "Kosher",     letter: "K", color: "#C39A5C" },
  { key: "halal",      label: "Halal",      letter: "H", color: "#C39A5C" },
];

export default function MobileProfile() {
  const { user, logout } = useAuth() || {};
  const [prefs] = useState(user?.food_preferences || ["vegetarian", "organic", "kosher", "halal"]);

  if (!user) return <Navigate to="/login?next=%2Fm%2Fprofile" replace />;

  return (
    <div className="pb-10" data-testid="mobile-profile">
      {/* Identity card */}
      <div className="mx-4 mt-3 bg-white rounded-2xl shadow-sm p-4 flex items-center gap-4">
        <div
          className="w-20 h-20 rounded-full bg-gray-200 overflow-hidden flex-shrink-0 border-2 border-white shadow"
        >
          {user.avatar_url ? (
            <img src={user.avatar_url} alt={user.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-2xl font-bold" style={{ color: FP_PURPLE }}>
              {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xl font-bold truncate" data-testid="profile-name">{user.name || "Food Pulse user"}</div>
          {user.email && (
            <div className="flex items-center gap-1.5 text-sm mt-1 text-gray-700" data-testid="profile-email">
              <Mail className="w-4 h-4" /> {user.email}
            </div>
          )}
          {user.phone && (
            <div className="flex items-center gap-1.5 text-sm mt-1 text-gray-700" data-testid="profile-phone">
              <Phone className="w-4 h-4" /> {user.phone}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="px-4 mt-4 space-y-3">
        <Link to="/profile/edit" className="flex items-center justify-between bg-gray-100 rounded-xl px-4 py-4" data-testid="profile-edit-link">
          <div className="flex items-center gap-3">
            <Pencil className="w-5 h-5" /> <span className="font-medium">Edit Profile</span>
          </div>
          <span className="opacity-60">›</span>
        </Link>
        <button className="w-full flex items-center justify-between bg-gray-100 rounded-xl px-4 py-4" data-testid="profile-delete-btn">
          <div className="flex items-center gap-3">
            <Trash2 className="w-5 h-5" /> <span className="font-medium">Delete Account</span>
          </div>
          <span className="opacity-60">›</span>
        </button>
        <button
          onClick={logout}
          data-testid="profile-logout-btn"
          className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl text-white font-semibold shadow"
          style={{ background: "#DE6259" }}
        >
          <LogOut className="w-5 h-5" /> Logout
        </button>
      </div>

      {/* Callout */}
      <p className="mx-4 mt-6 text-center text-sm font-semibold" style={{ color: FP_PURPLE }}>
        Update Your Food Pulse Profile with Important Information, organized in one place, you can access easily when needed.
      </p>

      {/* Food preferences */}
      <section className="mx-4 mt-5 bg-gray-100 rounded-2xl p-4" data-testid="profile-food-prefs">
        <h3 className="text-lg font-bold mb-3">Food Preferences</h3>
        <div className="grid grid-cols-2 gap-y-3">
          {DIET_TAGS.map((t) => (
            <div key={t.key} className="flex items-center gap-2">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-white text-sm font-bold"
                style={{ background: t.color }}
              >
                {t.Icon ? <t.Icon className="w-4 h-4" /> : t.letter}
              </div>
              <span className="text-sm">{t.label}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 text-sm">Allergies, etc.</div>
      </section>

      {/* Food assistance */}
      <section className="mx-4 mt-4 bg-gray-100 rounded-2xl p-4" data-testid="profile-food-assistance">
        <h3 className="text-lg font-bold mb-2">Food Assistance Program</h3>
        <p className="text-sm">
          Enrollment status and information about the programs users are enrolled in
        </p>
      </section>

      {/* Loyalty programs */}
      <section className="mx-4 mt-4 bg-gray-100 rounded-2xl p-4 mb-16" data-testid="profile-loyalty">
        <h3 className="text-lg font-bold mb-3">Loyalty Programs</h3>
        <div className="space-y-3">
          {[
            { label: "SUPERMARKET", color: "#2E6DC5" },
            { label: "STORE",       color: "#C43D2E" },
          ].map((card) => (
            <div key={card.label} className="flex items-center gap-3">
              <div
                className="w-16 h-12 rounded-lg text-white text-[10px] font-bold flex items-center justify-center text-center"
                style={{ background: card.color }}
              >
                {card.label}
              </div>
              <div className="flex-1 text-sm space-y-0.5">
                <div>Name</div>
                <div>Program Number</div>
                <div>Website</div>
                <div>Userid</div>
                <div>Password (optional)</div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
